/**
 * Shared by the six index providers (tasks, boards, documents, requests,
 * collections, calendar): §12.2 Hit → §12.1 Result, the open functions
 * (§3.5: the existing ones, no parallel paths) and the action lists (§2.5).
 *
 * Integration (A + B): the action builders, notices and page switch are the
 * palette core's (`../actions`, `palette.notice.*`, `openPage`), so an index
 * row behaves exactly like every other row; documents keep the card 5.4
 * badge (`utils/paletteDocScope`) — the workspace name when a document lives
 * in another scope than the Documents sidebar shows.
 */
import { Kanban, KeyRound, GitBranch, FolderSearch, CalendarDays } from 'lucide-react';
import { openAction, openNewTabAction, copyLinkAction, copyNameAction, copyWithNotice } from '../actions';
import { openPage } from '../providers/pages';
import { openDocumentTab, openTaskManagerBoardTab } from '../../store/tabsStore';
import { useTeamsStore } from '../../store/teamsStore';
import { useCollectionsStore } from '../../store/collectionsStore';
import { documentUri, boardUri } from '../../mcp/resources';
import { taskLink, openBoardLink } from '../../utils/boardLinks';
import { docScopeKey, docScopeBadge } from '../../utils/paletteDocScope';
import { docWorkspaceKey, normalizeWorkspaceKey } from '../../store/docWorkspaceKey';
import { branchNameFor } from '../../extensions/bundled/task-manager/taskKeys';
import i18n from '../../i18n';

const t = (key, opts) => i18n.t(key, opts);

export { docScopeKey };

export function teamIdOf(scope) {
  const s = String(scope || '');
  return s.startsWith('team:') ? s.slice(5) : null;
}

/** A hit's record scope → the documents store's scope descriptor (`rootScopeId`: its personal workspace). */
export function docScopeOf(hit) {
  const team = teamIdOf(hit.scope);
  if (team != null) return { type: 'team', teamId: team };
  if (String(hit.scope || '').startsWith('device')) return { type: 'device' };
  return hit.rootScopeId ? { type: 'personal', scopeId: hit.rootScopeId } : { type: 'personal' };
}

/**
 * Card 5.4 badge, the way the sidebar groups documents (`docWorkspaceKey`):
 * with the `documents.scopes` flag off a workspace document is in the one
 * Personal tree, so it gets no workspace badge there — and a Personal
 * document gets none while the sidebar shows a workspace key that the flag
 * folds into Personal.
 */
function documentBadge(scope, scopeName, currentKey) {
  const flagged = docWorkspaceKey(scope) === 'personal' && scope?.type === 'personal' ? { type: 'personal' } : scope;
  const teams = useTeamsStore.getState().teams || [];
  return docScopeBadge({ type: 'document', payload: { scope: flagged, scopeName } }, normalizeWorkspaceKey(currentKey || 'personal'), teams, t) || undefined;
}

function teamBadge(scope) {
  const id = teamIdOf(scope);
  if (id == null) return undefined;
  const team = (useTeamsStore.getState().teams || []).find((tm) => String(tm?.id) === id);
  return team?.name || t('palette.badge.team');
}

/** Detail without the key tokens (a task's detail is its keys). */
function stripKeys(detail, keyLabel) {
  if (!detail) return undefined;
  const rest = detail.split(' ').filter((w) => !/^[A-Za-z][A-Za-z0-9]{1,4}-[0-9]+$/.test(w) && w !== keyLabel);
  return rest.join(' ') || undefined;
}

/** Day of an event start (ISO instant or YYYY-MM-DD) as a local Date. */
export function eventDay(startAt) {
  if (!startAt) return null;
  const d = /^\d{4}-\d{2}-\d{2}$/.test(startAt) ? new Date(`${startAt}T00:00:00`) : new Date(startAt);
  return Number.isNaN(d.getTime()) ? null : d;
}

function eventWhen(startAt) {
  const d = eventDay(startAt);
  if (!d) return undefined;
  return startAt.length === 10 ? d.toLocaleDateString() : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * @param {string} providerId
 * @param {object} hit  §12.2 Hit
 * @param {{ names?: Map<string, string>, docs?: { currentKey?: string, scopeNames?: Map<string, string> } }} [extra]
 *   parent names (board of a task, collection of a request); for documents the
 *   sidebar's scope key and the personal workspaces' names
 */
export function hitToResult(providerId, hit, { names, docs } = {}) {
  const base = {
    provider: providerId,
    id: String(hit.id),
    title: hit.title || '',
    ts: hit.updatedAt || undefined,
    baseScore: typeof hit.relevance === 'number' ? hit.relevance : undefined,
  };
  const parentName = hit.parentId ? names?.get(String(hit.parentId)) : undefined;
  switch (hit.kind) {
    case 'task':
      return {
        ...base,
        keyLabel: hit.keyLabel || undefined,
        subtitle: parentName || stripKeys(hit.detail, hit.keyLabel),
        badge: teamBadge(hit.scope),
        searchFields: hit.detail ? [{ text: hit.detail, weight: 0.95 }] : undefined,
        payload: { kind: 'task', taskId: String(hit.id), boardId: hit.parentId || null, key: hit.keyLabel || null, teamId: teamIdOf(hit.scope) },
      };
    case 'board':
      return {
        ...base,
        subtitle: t('palette.kind.board'),
        badge: teamBadge(hit.scope),
        searchFields: hit.detail ? [{ text: hit.detail, weight: 0.6 }] : undefined,
        payload: { kind: 'board', boardId: String(hit.id), teamId: teamIdOf(hit.scope) },
      };
    case 'document':
    case 'folder': {
      const scope = docScopeOf(hit);
      const scopeName = scope.scopeId ? docs?.scopeNames?.get(String(scope.scopeId)) || null : null;
      const payload = { kind: hit.kind, documentId: String(hit.id), label: hit.title, scope, scopeName, blockId: hit.snippet?.blockId || null };
      return {
        ...base,
        subtitle: hit.detail || t(hit.kind === 'folder' ? 'palette.index.kind.folder' : 'palette.kind.document'),
        badge: documentBadge(scope, scopeName, docs?.currentKey),
        snippet: hit.snippet ? { text: hit.snippet.text, ranges: hit.snippet.ranges || [] } : undefined,
        payload,
      };
    }
    case 'request': {
      const url = hit.detail && hit.method && hit.detail.startsWith(`${hit.method} `) ? hit.detail.slice(hit.method.length + 1) : hit.detail;
      return {
        ...base,
        method: hit.method || 'GET',
        subtitle: [parentName, url].filter(Boolean).join(' · ') || undefined,
        searchFields: url ? [{ text: url, weight: 0.6 }] : undefined,
        payload: { kind: 'request', requestId: String(hit.id), collectionId: hit.parentId || null, workspaceId: hit.scope },
      };
    }
    case 'collection':
      return { ...base, subtitle: t('palette.kind.collection'), payload: { kind: 'collection', collectionId: String(hit.id), workspaceId: hit.scope } };
    case 'event':
      return {
        ...base,
        subtitle: [eventWhen(hit.startAt), hit.detail].filter(Boolean).join(' · ') || t('palette.index.kind.event'),
        badge: teamBadge(hit.scope),
        searchFields: hit.detail ? [{ text: hit.detail, weight: 0.6 }] : undefined,
        payload: { kind: 'event', eventId: String(hit.id), startAt: hit.startAt || null },
      };
    default:
      return { ...base, subtitle: hit.detail || undefined, payload: { kind: hit.kind } };
  }
}

// ------------------------------------------------------------------ opening

async function documentsUi() {
  const { useDocumentsUiStore } = await import('../../extensions/bundled/documents/documentsUiStore');
  return useDocumentsUiStore.getState();
}

function findRequest(id) {
  for (const c of useCollectionsStore.getState().collections || []) {
    const r = (c.requests || []).find((x) => String(x.id) === String(id));
    if (r) return r;
  }
  return null;
}

function findCollection(id) {
  return (useCollectionsStore.getState().collections || []).find((c) => String(c.id) === String(id)) || null;
}

export const open = {
  async document(r) {
    const p = r.payload;
    openDocumentTab({ documentId: p.documentId, label: p.label, scope: p.scope });
    const ui = await documentsUi().catch(() => null);
    // The sidebar's key for that tree (flag-aware, as revealInSidebar does).
    ui?.setScopeKey?.(docWorkspaceKey(p.scope));
    // A body hit opens the document at the top: the editor has no block
    // anchor yet (its block ids live in LexicalRichEditor's key map only);
    // `blockId` rides in the payload for when it does.
    openPage('documents.panel');
  },
  async folder(r) {
    const ui = await documentsUi().catch(() => null);
    ui?.setScopeKey?.(docWorkspaceKey(r.payload.scope));
    openPage('documents.panel');
  },
  board(r) {
    openTaskManagerBoardTab({ boardId: r.payload.boardId, label: r.title });
    openPage('task-manager.panel');
  },
  task(r) {
    return openBoardLink({ boardId: r.payload.boardId, taskId: r.payload.taskId });
  },
  request(r, ctx) {
    const req = findRequest(r.payload.requestId);
    if (req) ctx?.nav?.onSelectRequest?.(req);
    openPage('collections');
  },
  collection(r, ctx) {
    const col = findCollection(r.payload.collectionId);
    if (col) ctx?.nav?.onOpenCollection?.(col);
    openPage('collections');
  },
  /** "Open event" did not exist (PLAN §1.3): the calendar on that day, day view, where its start time is. */
  async event(r) {
    const { useCalendarUiStore } = await import('../../extensions/bundled/calendar/calendarUiStore');
    const day = eventDay(r.payload.startAt);
    const ui = useCalendarUiStore.getState();
    if (day) ui.setFocusedDate(day);
    ui.setView?.('day');
    openPage('calendar.panel');
  },
};

// ------------------------------------------------------------------ actions

const wireScope = (teamId) => (teamId != null ? `team:${teamId}` : 'personal');
const docWireScope = (scope) => (scope?.type === 'team' && scope.teamId != null ? `team:${scope.teamId}` : 'personal');

/** [0] is the Enter default (§12.1). */
export function actionsFor(r) {
  switch (r.payload?.kind) {
    case 'task': {
      const list = [
        openAction((x) => open.task(x)),
        { id: 'openBoard', titleKey: 'palette.action.openBoard', icon: Kanban, run: (x) => openBoardLink({ boardId: x.payload.boardId }) },
      ];
      if (r.payload.key) {
        list.push({
          id: 'copyTaskKey',
          titleKey: 'palette.action.copyTaskKey',
          icon: KeyRound,
          shortcut: { mod: true, shift: true, key: 'c' },
          run: (x) => copyWithNotice(x.payload.key, t('palette.notice.taskKey')),
        });
        list.push({
          id: 'copyBranch',
          titleKey: 'palette.action.copyBranch',
          icon: GitBranch,
          run: (x) => copyWithNotice(branchNameFor(x.payload.key, x.title) || x.payload.key, t('palette.notice.branch')),
        });
      }
      list.push(copyLinkAction((x) => taskLink({ teamId: x.payload.teamId, boardId: x.payload.boardId, taskId: x.payload.taskId })));
      return list;
    }
    case 'board':
      return [
        openAction((x) => open.board(x)),
        openNewTabAction((x) => open.board(x)),
        copyLinkAction((x) => boardUri(wireScope(x.payload.teamId), x.payload.boardId)),
        copyNameAction(),
      ];
    case 'document':
      return [
        openAction((x) => open.document(x)),
        openNewTabAction((x) => open.document(x)),
        copyLinkAction((x) => documentUri(docWireScope(x.payload.scope), x.payload.documentId)),
        copyNameAction(),
        { id: 'reveal', titleKey: 'palette.action.reveal', icon: FolderSearch, run: (x) => open.folder(x) },
      ];
    case 'folder':
      return [openAction((x) => open.folder(x)), copyNameAction()];
    case 'request':
      return [openAction((x, ctx) => open.request(x, ctx)), openNewTabAction((x, ctx) => open.request(x, ctx)), copyNameAction()];
    case 'collection':
      return [openAction((x, ctx) => open.collection(x, ctx)), copyNameAction()];
    case 'event':
      return [{ ...openAction((x) => open.event(x)), icon: CalendarDays }, copyNameAction()];
    default:
      return [];
  }
}
